import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { token } from "../theme/tokens.js";
export function GalleryCard({ item, onClick, renderContent, renderActions, renderBadge, renderStatusActions, onDismissStatus, onRetryStatus, onVisible, }) {
    const [hovered, setHovered] = useState(false);
    const [keyboardFocused, setKeyboardFocused] = useState(false);
    const [touchActions, setTouchActions] = useState(false);
    const [dismissHovered, setDismissHovered] = useState(false);
    const [retryHovered, setRetryHovered] = useState(false);
    const cardRef = useRef(null);
    const keyboardInteractionRef = useRef(false);
    useEffect(() => {
        if (typeof window.matchMedia !== "function")
            return;
        const query = window.matchMedia("(hover: none), (pointer: coarse)");
        const update = () => setTouchActions(query.matches);
        update();
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
    }, []);
    useEffect(() => {
        const element = cardRef.current;
        if (!element || !onVisible || typeof IntersectionObserver === "undefined")
            return;
        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting))
                return;
            onVisible();
            observer.disconnect();
        }, { root: findScrollParent(element), rootMargin: "240px 0px" });
        observer.observe(element);
        return () => observer.disconnect();
    }, [onVisible]);
    const handleKeyDown = useCallback((e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault();
            onClick();
        }
    }, [onClick]);
    const cardStyle = {
        width: "100%",
        borderRadius: token("cardRadius"),
        border: `1px solid ${token("cardBorder")}`,
        background: token("cardBg"),
        boxShadow: hovered ? token("cardShadowHover") : token("cardShadow"),
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        cursor: onClick ? "pointer" : "default",
        overflow: "hidden",
        position: "relative",
    };
    const thumbnailStyle = {
        width: "100%",
        aspectRatio: "4 / 3",
        background: token("cardThumbnailBg"),
        overflow: "hidden",
        position: "relative",
    };
    const imgStyle = {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
    };
    const bodyStyle = {
        padding: "12px",
    };
    const titleStyle = {
        margin: 0,
        fontSize: "14px",
        fontWeight: 600,
        color: token("titleColor"),
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };
    const badgeStyle = {
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
    };
    const actionsStyle = {
        position: "absolute",
        top: 8,
        left: 8,
        right: 8,
        zIndex: 1,
        display: hovered || keyboardFocused || touchActions ? "flex" : "none",
        gap: "4px",
        pointerEvents: "none",
    };
    const cardStatus = item.cardStatus;
    const canOpen = Boolean(onClick) && !cardStatus;
    const canDismissStatus = cardStatus?.kind === "error" && Boolean(onDismissStatus);
    const canRetryStatus = cardStatus?.kind === "error" && Boolean(onRetryStatus);
    const canFocusStatusActions = cardStatus?.kind === "error" &&
        (Boolean(renderStatusActions) || canDismissStatus || canRetryStatus);
    return (_jsxs("div", { ref: cardRef, style: cardStyle, role: canOpen ? "button" : undefined, tabIndex: canOpen || canFocusStatusActions ? 0 : undefined, "aria-label": item.title, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), onPointerDownCapture: () => {
            keyboardInteractionRef.current = false;
            setKeyboardFocused(false);
        }, onKeyDownCapture: () => {
            keyboardInteractionRef.current = true;
            setKeyboardFocused(true);
        }, onFocus: (event) => {
            if (keyboardInteractionRef.current || event.target.matches(":focus-visible")) {
                setKeyboardFocused(true);
            }
        }, onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setKeyboardFocused(false);
        }, onClick: canOpen ? onClick : undefined, onKeyDown: canOpen ? handleKeyDown : undefined, children: [_jsxs("div", { style: thumbnailStyle, children: [cardStatus ? (_jsxs("div", { role: cardStatus.kind === "error" ? "alert" : "status", style: {
                            display: "flex",
                            height: "100%",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            color: cardStatus.kind === "error" ? "var(--exms-danger-text)" : token("titleColor"),
                            background: cardStatus.kind === "error" ? "var(--exms-danger-soft)" : token("cardThumbnailBg"),
                            textAlign: "center",
                            padding: 12,
                            boxSizing: "border-box",
                            overflowWrap: "anywhere",
                        }, children: [_jsx("span", { "aria-hidden": "true", children: cardStatus.kind === "error" ? "⚠" : "◌" }), _jsxs("span", { children: [cardStatus.message, canRetryStatus && (_jsxs(_Fragment, { children: [" ", _jsx("button", { type: "button", "aria-label": `Try again: ${item.title}`, title: "Try again", onClick: (event) => {
                                                    event.stopPropagation();
                                                    onRetryStatus?.(item);
                                                }, onKeyDown: (event) => event.stopPropagation(), onMouseEnter: () => setRetryHovered(true), onMouseLeave: () => setRetryHovered(false), style: {
                                                    ...iconButtonStyle,
                                                    display: hovered || keyboardFocused || touchActions ? "inline-flex" : "none",
                                                    verticalAlign: "middle",
                                                    width: 24,
                                                    height: 24,
                                                    background: retryHovered ? iconButtonHoverBg : "transparent",
                                                }, children: _jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "16", height: "16", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M20 11a8.1 8.1 0 0 0-15.5-2M4 5v4h4M4 13a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" }) }) })] }))] })] })) : (_jsxs(_Fragment, { children: [item.thumbnailUrl && _jsx("img", { src: item.thumbnailUrl, alt: item.title, style: imgStyle }), renderBadge && _jsx("div", { style: badgeStyle, children: renderBadge(item) })] })), ((!cardStatus && renderActions) ||
                        (cardStatus?.kind === "error" && renderStatusActions)) && (_jsx("div", { style: actionsStyle, onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), role: "toolbar", "aria-label": cardStatus ? "Error actions" : "Card actions", children: _jsx("div", { style: { width: "100%", pointerEvents: "auto" }, children: cardStatus ? renderStatusActions?.(item) : renderActions?.(item) }) })), canDismissStatus && (_jsx("button", { type: "button", "aria-label": `Dismiss ${item.title}`, title: "Dismiss", onClick: (event) => {
                            event.stopPropagation();
                            onDismissStatus?.(item);
                        }, onKeyDown: (event) => event.stopPropagation(), onMouseEnter: () => setDismissHovered(true), onMouseLeave: () => setDismissHovered(false), style: {
                            ...iconButtonStyle,
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            display: hovered || keyboardFocused || touchActions ? "flex" : "none",
                            width: 28,
                            height: 28,
                            background: dismissHovered ? iconButtonHoverBg : "transparent",
                        }, children: _jsx("svg", { "aria-hidden": "true", viewBox: "0 0 20 20", width: "16", height: "16", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M15 5 5 15M5 5l10 10" }) }) }))] }), " ", _jsxs("div", { style: bodyStyle, children: [_jsx("p", { style: titleStyle, children: item.title }), renderContent &&
                        (cardStatus ? (_jsx("div", { "aria-hidden": "true", style: { marginTop: "4px", visibility: "hidden" }, children: renderContent(item) })) : (_jsx("div", { style: { marginTop: "4px", color: token("metaColor") }, children: renderContent(item) })))] })] }));
}
const iconButtonStyle = {
    padding: 0,
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    color: "var(--exms-danger-text)",
};
const iconButtonHoverBg = "color-mix(in srgb, currentColor 14%, transparent)";
function findScrollParent(element) {
    let parent = element.parentElement;
    while (parent) {
        const overflowY = getComputedStyle(parent).overflowY;
        if (overflowY === "auto" || overflowY === "scroll")
            return parent;
        parent = parent.parentElement;
    }
    return null;
}
//# sourceMappingURL=GalleryCard.js.map